import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { RouterProvider } from "react-router-dom";

import { router } from "@/router";
import { ConfirmProvider } from "@/hooks/useConfirm";
import { ToastProvider } from "@/hooks/useToast";
import { AppThemeProvider } from "@/theme/orgBrand";
import {
  KeyboardShortcutsModal,
  useKeyboardShortcutsListener,
} from "@/features/help/KeyboardShortcuts";
import "@/theme/fonts.css";
import { i18nReady } from "@/i18n";
import { firstRenderGate } from "@/i18n/firstRender";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
    mutations: { retry: 0 },
  },
});

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("#root element missing");
}

/** Global-scope ``?`` shortcut so the cheatsheet is reachable from
 *  any route. Listener + modal travel together — the listener owns
 *  the open-state, the modal renders it. Mounted as a sibling of
 *  ``RouterProvider`` so nothing remounts on navigation.
 *  This is the app entry — no Fast Refresh applies, the warning
 *  about "file has exports" is for HMR which doesn't replace
 *  ``main.tsx``. */
// eslint-disable-next-line react-refresh/only-export-components
function Root(): React.JSX.Element {
  const shortcuts = useKeyboardShortcutsListener();
  return (
    <>
      <RouterProvider router={router} />
      <KeyboardShortcutsModal
        open={shortcuts.open}
        onClose={() => shortcuts.setOpen(false)}
      />
    </>
  );
}

// Render once the start language's bundle is in, so a sv/fi user never
// sees a flash of English. i18nReady never rejects: a failed chunk
// (stale SPA) renders in English instead of not at all. A chunk that
// stalls rather than fails gets FIRST_RENDER_WAIT_MS, then the app
// renders in English and switches when the bundle arrives.
void firstRenderGate(i18nReady).then(() => {
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <AppThemeProvider>
          <ToastProvider>
            <ConfirmProvider>
              <Root />
            </ConfirmProvider>
          </ToastProvider>
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </AppThemeProvider>
      </QueryClientProvider>
    </React.StrictMode>,
  );
});
